/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Stack } from '@fluentui/react/lib/Stack';
import React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';

export type EditUtteranceModalProps = BasicModalProps<boolean>;

const messages = defineMessages({
    modalTitle: {
        id: 'entityDetails.editUtteranceModal.modalTitle',
        defaultMessage: 'Editing this utterance will remove its labels. Continue?'
    },
    modalOption: {
        id: 'entityDetails.editUtteranceModal.modalOption',
        defaultMessage: 'Don’t show me this again.'
    },
    footerSubmitButton: {
        id: 'entityDetails.editUtteranceModal.footerSubmitButton',
        defaultMessage: 'Yes, remove my labels'
    }
});

/**
 * A modal that opens on editing an utterance from the entity examples table.
 */
export const EditUtteranceModal = (props: EditUtteranceModalProps) => {
    const { onDismiss, onSubmit } = props;

    const [isCheckboxChecked, setIsCheckboxChecked] = React.useState(false);

    const { localize } = useLocalization();

    const onRenderFooter = () => (
        <Stack horizontal tokens={{ childrenGap: 10 }} horizontalAlign="end">
            <PrimaryButton onClick={() => onSubmit(isCheckboxChecked)}>{localize(messages.footerSubmitButton)}</PrimaryButton>
            <DefaultButton onClick={onDismiss}>{localize(globalMessages.cancel)}</DefaultButton>
        </Stack>
    );

    return (
        <BasicModal {...props} onRenderFooter={onRenderFooter} dialogContentProps={{ title: localize(messages.modalTitle) }}>
            <Checkbox
                label={localize(messages.modalOption)}
                checked={isCheckboxChecked}
                onChange={(_ev, checked) => setIsCheckboxChecked(checked)}
            />
        </BasicModal>
    );
};
